/**
 * The message the API gave for a failed call, or a plain fallback.
 *
 * The generated clients return `{ data, error }` rather than throwing, so a call that forgets to
 * check `error` and falls back to a default value turns every 403, 404 and 500 into a successful
 * empty result. An empty list then means "nothing here" and "could not reach the server" at the
 * same time, and the screen shows an onboarding empty state to a user whose data exists but is
 * unreadable. Throwing is what lets react-query report the failure.
 *
 * `reason` first because the brand ingest 422 carries its explanation there, and that sentence is
 * the only account of why nothing could be read from a document.
 */
/**
 * Nest reports a failed DTO as `message: ValidationError[]`, nested one level per object property.
 *
 * Flattened here because the alternative is what shipped: every validation 400 fell through the
 * string checks below and surfaced as the generic fallback, so a rejected field was indistinguishable
 * from a server being down and the only way to find out was reading the server log.
 */
function fieldErrors(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const walk = (node: unknown, path: string[]): void => {
    if (typeof node !== 'object' || node === null) return;
    const error = node as {
      property?: string;
      constraints?: Record<string, string>;
      children?: unknown[];
    };
    const here = error.property ? [...path, error.property] : path;
    if (error.constraints) {
      for (const message of Object.values(error.constraints)) {
        out.push(here.length > 1 ? `${here.join('.')}: ${message}` : message);
      }
    }
    for (const child of error.children ?? []) walk(child, here);
  };
  for (const entry of value) walk(entry, []);
  return out;
}

export function describeApiFailure(
  error: unknown,
  // Last-resort default for callers that don't pass their own fallback; every call site in
  // this codebase already supplies one.
  // eslint-disable-next-line i18next/no-literal-string
  fallback = 'The request failed.',
): string {
  if (typeof error === 'object' && error !== null) {
    const body = error as { reason?: unknown; detail?: unknown; message?: unknown };
    // Field errors first: "sources.0.fileSizeBytes must be a positive number" tells somebody what to
    // change, and "Bad Request Exception" does not.
    const fields = fieldErrors(body.message);
    if (fields.length > 0) return fields.join('; ');
    for (const field of [body.reason, body.detail, body.message]) {
      if (typeof field === 'string' && field.trim().length > 0) return field;
    }
  }
  if (typeof error === 'string' && error.trim().length > 0) return error;
  return fallback;
}
